/* Analytics tracking with Supabase persistence */
import { getTelegramUser } from "@/lib/telegram";

/**
 * Lazy-load the Supabase client so the ~190 KB chunk is excluded from the
 * initial route. It only loads on first event/lead, after the LCP frame.
 */
let _supabasePromise: Promise<typeof import("@/integrations/supabase/client")["supabase"]> | null = null;
function getSupabase() {
  if (!_supabasePromise) {
    _supabasePromise = import("@/integrations/supabase/client").then((m) => m.supabase);
  }
  return _supabasePromise;
}


export const appState = {
  telegram_user_id: "",
  telegram_username: "",
  source_button: "",
  viewed_category: "",
  viewed_product: "",
  lead_type: "",
};

/** Populate appState from Telegram user data */
export function syncTelegramUser() {
  const user = getTelegramUser();
  appState.telegram_user_id = user.id;
  appState.telegram_username = user.username;
}

/** Push a clean GTM event with standard schema (event_name, button_text, page_url) */
export function pushDataLayerEvent(
  event: string,
  params?: { button_text?: string; [key: string]: unknown },
) {
  try {
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event,
      event_name: event,
      button_text: params?.button_text ?? "",
      page_url: window.location.href,
      ...(params || {}),
    });
  } catch (e) {
    console.warn("[ZebraJaluzi] dataLayer push failed:", e);
  }
}

/** Google Ads configuration */
export const GOOGLE_ADS_ID = "AW-17941564843";
/** Default conversion label for general lead/click conversions. Leave empty if unused. */
export const GOOGLE_ADS_CONVERSION_LABEL = "";
/** Page-view conversion label ("Просмотр страницы") */
export const GOOGLE_ADS_PAGEVIEW_LABEL = "D9WoCKSinvUbEKubmutC";

type GtagFn = (...args: unknown[]) => void;

interface AdsConversionOptions {
  label?: string;
  value?: number;
  currency?: string;
  dedupeKey?: string;
}

/** Fire a Google Ads conversion exactly once per (action+dedupeKey). */
const _firedConversions = new Set<string>();
export function trackAdsConversion(action: string, optsOrKey?: string | AdsConversionOptions) {
  const opts: AdsConversionOptions = typeof optsOrKey === "string" ? { dedupeKey: optsOrKey } : (optsOrKey || {});
  const key = `${action}::${opts.dedupeKey ?? ""}`;
  if (_firedConversions.has(key)) return;
  _firedConversions.add(key);

  const label = opts.label ?? GOOGLE_ADS_CONVERSION_LABEL;
  const sendTo = label ? `${GOOGLE_ADS_ID}/${label}` : GOOGLE_ADS_ID;

  const params: Record<string, unknown> = { send_to: sendTo };
  if (typeof opts.value === "number") params.value = opts.value;
  if (opts.currency) params.currency = opts.currency;

  try {
    const w = window as unknown as { gtag?: GtagFn; dataLayer?: Record<string, unknown>[] };
    if (typeof w.gtag === "function") {
      w.gtag("event", "conversion", params);
    } else {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "ads_conversion", action, ...params });
    }
  } catch (e) {
    console.warn("[ZebraJaluzi] Ads conversion failed:", e);
  }
}

/** Fire the Google Ads page-view conversion (once per page load). */
export function trackAdsPageView() {
  trackAdsConversion("page_view", {
    label: GOOGLE_ADS_PAGEVIEW_LABEL,
    value: 1.0,
    currency: "USD",
    dedupeKey: window.location.pathname,
  });
}

/**
 * Fire a Google Ads conversion on click and then navigate (or run callback).
 * Mirrors Google's `gtag_report_conversion` snippet.
 * Use for outbound links so the conversion is recorded before page change.
 */
export function reportAdsConversionAndNavigate(url?: string, label: string = GOOGLE_ADS_PAGEVIEW_LABEL) {
  const sendTo = label ? `${GOOGLE_ADS_ID}/${label}` : GOOGLE_ADS_ID;
  const navigate = () => {
    if (url) window.location.href = url;
  };

  try {
    const w = window as unknown as { gtag?: GtagFn; dataLayer?: Record<string, unknown>[] };
    if (typeof w.gtag === "function") {
      // Safety net: navigate even if gtag callback never fires (e.g. blocked)
      let navigated = false;
      const safeNavigate = () => { if (!navigated) { navigated = true; navigate(); } };
      setTimeout(safeNavigate, 1500);
      w.gtag("event", "conversion", {
        send_to: sendTo,
        value: 1.0,
        currency: "USD",
        event_callback: safeNavigate,
      });
    } else {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "ads_conversion", action: "click", send_to: sendTo, value: 1.0, currency: "USD" });
      navigate();
    }
  } catch (e) {
    console.warn("[ZebraJaluzi] Ads conversion (navigate) failed:", e);
    navigate();
  }
  return false;
}

/** Detect where the visitor came from; cached per browser session. */
export function getTrafficSource(): string {
  try {
    const cached = sessionStorage.getItem("zj_src");
    if (cached) return cached;
    const p = new URLSearchParams(window.location.search);
    const ref = document.referrer.toLowerCase();
    const utm = (p.get("utm_source") || "").toLowerCase();
    const map: [RegExp, string][] = [
      [/google/, "Google"], [/yandex|ya\.ru/, "Yandex"], [/instagram/, "Instagram"],
      [/facebook|fb\./, "Facebook"], [/t\.me|telegram/, "Telegram"], [/tiktok/, "TikTok"],
      [/youtube|youtu\.be/, "YouTube"], [/bing/, "Bing"], [/chatgpt|openai/, "ChatGPT"],
      [/perplexity/, "Perplexity"], [/gemini/, "Gemini"], [/olx/, "OLX"], [/2gis/, "2GIS"],
    ];
    let src = "";
    if (p.get("gclid")) src = "Google Ads";
    else if (p.get("yclid")) src = "Yandex Direct";
    else if (p.get("fbclid")) src = "Facebook";
    const probe = utm || ref;
    if (!src && probe) src = map.find(([r]) => r.test(probe))?.[1] || (utm ? utm : (() => { try { return new URL(ref).hostname.replace(/^www\./, ""); } catch { return ""; } })());
    if (!src && getTelegramUser().id) src = "Telegram";
    if (!src || src.includes("zebrajaluzi")) src = "To'g'ridan-to'g'ri";
    sessionStorage.setItem("zj_src", src);
    return src;
  } catch {
    return "Noma'lum";
  }
}

/** Track event — logs to console and saves to Supabase events table */
export async function trackEvent(event: string, extra?: Record<string, unknown>) {
  const payload = {
    event_name: event,
    source_button: appState.source_button,
    viewed_category: appState.viewed_category,
    viewed_product: appState.viewed_product,
    lead_type: appState.lead_type,
    telegram_user_id: appState.telegram_user_id || "",
    telegram_username: appState.telegram_username || "",
    traffic_source: getTrafficSource(),
    landing_path: window.location.pathname,
  };

  console.log(`[ZebraJaluzi] ${event}`, JSON.stringify({ ...payload, timestamp: new Date().toISOString(), ...extra }, null, 2));

  // Push to GTM dataLayer so GTM can forward to GA4 / other tags
  try {
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...payload, ...(extra || {}) });
  } catch (e) {
    console.warn("[ZebraJaluzi] dataLayer push failed:", e);
  }

  const { error } = await (await getSupabase()).from("events").insert(payload);
  if (error) console.error("[ZebraJaluzi] Failed to save event:", error.message);
}

/** Send Telegram notification about new lead */
async function notifyTelegram(data: Record<string, string>) {
  try {
    const { error } = await (await getSupabase()).functions.invoke("notify-telegram", { body: data });
    if (error) console.error("[ZebraJaluzi] Telegram notify failed:", error.message);
  } catch (e) {
    console.error("[ZebraJaluzi] Telegram notify error:", e);
  }
}

/** Save lead to Supabase leads table */
export async function saveLead(data: {
  name: string;
  phone: string;
  interest: string;
  comment: string;
  lead_type: string;
  source_button: string;
  viewed_product: string;
  telegram_user_id: string;
  telegram_username: string;
  created_at?: string;
  screenshot_url?: string;
}) {
  console.log("[ZebraJaluzi] submit_lead", JSON.stringify(data, null, 2));

  const { error } = await (await getSupabase()).from("leads").insert({ ...data, traffic_source: getTrafficSource() });
  if (error) {
    console.error("[ZebraJaluzi] Failed to save lead:", error.message);
    return false;
  }

  // Send Telegram notification (fire-and-forget)
  notifyTelegram(data);

  // Send webhook to external automation (Make.com)
  await notifyWebhook(data);

  return true;
}

async function notifyWebhook(data: {
  name: string;
  phone: string;
  interest: string;
  comment: string;
  screenshot_url?: string;
}) {
  try {
    const { error } = await (await getSupabase()).functions.invoke("notify-webhook", {
      body: {
        name: data.name,
        phone: data.phone,
        interest: data.interest,
        comment: data.comment || "",
        screenshot_url: data.screenshot_url || "",
      },
    });
    if (error) console.warn("[ZebraJaluzi] Webhook error:", error.message);
  } catch (e) {
    console.warn("[ZebraJaluzi] Webhook error:", e);
  }
}
